import BarChartIcon from '@mui/icons-material/BarChart';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useMediaQuery
} from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  extractGainMax,
  extractPodium,
  extractReculValue,
  isSortOption
} from '../../../../utils/format';
import { usePronoCache } from '../../providers/PronoCacheProvider';
import { Discipline, QuickFilterType, SORT_OPTION, Status } from '../../services/enum';
import useGetPronos from '../../services/use-get_pronos';
import HeaderProno from './HeaderProno';
import PronoCard from './PronoCard';
import useGetCourses from '../../services/use-get-courses';
import SectionTitle from '../../../../core/SectionTitle';
import { isBase, isCrack, isOutsider } from '../../../../utils/prono';
import QuickFilterButtons from './QuickFilters';

const PronoPage: FC = () => {
  const { date, r, c } = useParams<{
    date: string;
    r: string;
    c: string;
  }>();


  const { data: program } = useGetCourses(date || "");
  const { data: pronos, isLoading, isError, error } = useGetPronos(date || "", Number(r), Number(c));
  const { hasProno } = usePronoCache();
  const reunion = program?.reunions.find(reu => reu.numOfficiel === Number(r));
  const course = reunion?.courses.find(cou => cou.numOrdre === Number(c))
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const [searchParam, setSearchParams] = useSearchParams();
  const [sortSpecific, setSortBySpecific] = useState(searchParam.get('sort') as SORT_OPTION || SORT_OPTION.SCORE);
  const [quickFilter, setQuickFilter] = useState<QuickFilterType>(QuickFilterType.ALL);
  const plafond = extractGainMax(course?.conditions || "");
  const recul = extractReculValue(course?.conditions || "", plafond);

  useEffect(() => {
    const sort = searchParam.get('sort');
    if (sort && isSortOption(sort)) {
      setSortBySpecific(sort);
    }
  }, [searchParam]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="50vh"
      >
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  // ❌ 404 → course inexistante
  if (isError && error.status === 404) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="50vh"
        gap={2}
      >
        <Typography variant="h6">
          Course introuvable
        </Typography>
        <Typography color="text.secondary">
          Cette course n’existe pas ou n’est pas disponible.
        </Typography>

        <Button
          variant="contained"
          onClick={() => navigate(-1)}
        >
          <Typography>Retour</Typography>
        </Button>
      </Box>
    );
  }

  // 💥 Autre erreur
  if (isError) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="50vh"
      >
        <Typography color="error">
          Une erreur est survenue lors du chargement des pronostics
        </Typography>
      </Box>
    );
  }

  // ⛔ Sécurité supplémentaire (données manquantes)
  if (!course || !reunion || !pronos) {
    return null;
  }

  const sortOptions =
    course.discipline === Discipline.ATTELE ||
      course.discipline === Discipline.MONTE
      ? SORT_OPTION
      : Object.values(SORT_OPTION).filter((o) => o !== SORT_OPTION.RED);

  const pronoPartant =
    pronos
      ?.filter((p) => p.data.status === Status.PARTANT)
      .filter((p) => {
        if (quickFilter === QuickFilterType.CRACK) return isCrack(p);
        if (quickFilter === QuickFilterType.BASE) return isBase(p);
        if (quickFilter === QuickFilterType.OUTSIDER) return isOutsider(p);
        return true; // ALL
      })
      .sort((a, b) => {
        if (sortSpecific === SORT_OPTION.RED)
          return (a.performance.averageRed || 100000) - (b.performance.averageRed || 100000);
        else if (sortSpecific === SORT_OPTION.PROBABILITY)
          return (b.probability || 0) - (a.probability || 0);
        else if (sortSpecific === SORT_OPTION.COTE)
          return (a.data.coteDirect || 999) - (b.data.coteDirect || 999);
        else if (sortSpecific === SORT_OPTION.ELO)
          return (b.performance.elo || 0) - (a.performance.elo || 0);
        else if (sortSpecific === SORT_OPTION.SCORE_PUR)
          return (b.totalPur || 0) - (a.totalPur || 0);
        else
          return (b.total) - (a.total);
      }) || [];
  const podium = extractPodium(course);

  const getLabel = (value: SORT_OPTION): string => {
    if (value === SORT_OPTION.PROBABILITY) return 'Probabilité';
    else if (value === SORT_OPTION.RED) return 'Réduction Km';
    else if (value === SORT_OPTION.COTE) return 'Cote';
    else if (value === SORT_OPTION.ELO) return 'Elo';
    else if (value === SORT_OPTION.SCORE_PUR) return 'Score pur';
    else return 'Score';
  };

  const handlePronoClick = (num: number) => {
    navigate(`history/${num}`, { state: { modal: true } });
  }

  const handleChangeSort = (value: SORT_OPTION) => {
    const params = new URLSearchParams(searchParam);
    params.set('sort', String(value));
    setSearchParams(params, { replace: true });
  }

  pronoPartant.forEach(p => {
    console.log(p.data.numero, p)
  })

  return (
    <Box display={'flex'} flexDirection={'column'} width={isMobile ? "100%" : "80%"} justifyContent={'center'} mt={1}>
      <Box display={'flex'} flexDirection={'column'} p={1} gap={2}>
        <HeaderProno reunion={reunion} course={course} />
        <QuickFilterButtons activeFilter={quickFilter} pronos={pronos} onFilterChange={setQuickFilter} />
        <Box
          display={'flex'}
          flexDirection={'row'}
          justifyContent={'space-between'}
        >
          <SectionTitle icon={BarChartIcon} label="Pronos" />
          <FormControl sx={{ width: '150px', mr: '24px' }} size="small">
            <InputLabel id="sortBy" color="secondary">
              Trier par
            </InputLabel>
            <Select
              labelId="sort"
              id="sort"
              value={sortSpecific}
              label="Trier par"
              onChange={(event) =>
                handleChangeSort(event.target.value as SORT_OPTION)
              }
            >
              {Object.values(sortOptions).map((s) => {
                return (
                  <MenuItem key={s} value={s}>
                    {getLabel(s)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>
        <Box
          display={'flex'}
          flexDirection={'row'}
          gap={3}
          flexWrap="wrap"
          justifyContent={'center'}
        >
          {pronoPartant.map((p) => {
            return (
              <PronoCard
                key={p.data.numero}
                prono={p}
                isOnPodium={podium.includes(String(p.data.numero))}
                sortSelected={sortSpecific}
                onClick={handlePronoClick}
                isMarked={hasProno(p, reunion, course)}
                plafond={plafond || undefined}
                recul={recul || undefined}
              />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default PronoPage;
